import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { scrollToSection } from './shared';
import { track } from '../../lib/analytics';

function useActiveSection(ids) {
  const [active, setActive] = useState('');
  useEffect(() => {
    const els = ids.map(id => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-35% 0px -55% 0px', threshold: [0, 0.05, 0.25] });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [ids.join(',')]);
  return active;
}

export default function LandingHeader({ nav, onCta }) {
  const links = (nav.links || []).filter(l => l.label && l.target);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useActiveSection(links.map(l => l.target));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    track('nav_click', { target });
    scrollToSection(target);
  };

  return (
    <header data-testid="landing-header"
      className={`transition-all duration-300 ${scrolled || open ? 'bg-[#f5f1eb]/90 backdrop-blur-xl border-b border-slate-900/5 shadow-[0_8px_30px_-20px_rgba(15,23,42,0.35)]' : 'bg-transparent border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-[72px] flex items-center justify-between gap-6">
        <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2.5 shrink-0" data-testid="landing-logo">
          <img src="/logo-navbar.png" alt="CELPIPMax" className="w-9 h-9" />
          <span className="font-[Manrope] font-extrabold text-lg text-slate-900 tracking-tight">CELPIPMax</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1" aria-label="Page sections" data-testid="landing-nav-desktop">
          {links.map(l => (
            <button key={l.target} onClick={() => go(l.target)} data-testid={`nav-link-${l.target}`}
              className={`relative px-3.5 py-2 text-[14px] font-semibold rounded-full transition-colors ${active === l.target ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
              {l.label}
              <span className={`absolute left-3.5 right-3.5 -bottom-0.5 h-[2px] rounded-full bg-amber-500 transition-all duration-300 ${active === l.target ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`} />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Link to="/login" className="px-3.5 py-2 text-[14px] font-semibold text-slate-700 hover:text-slate-900 transition-colors" data-testid="landing-nav-login">{nav.login_label || 'Log in'}</Link>
          <button onClick={() => onCta('header')} data-testid="landing-nav-cta"
            className="group inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-extrabold uppercase tracking-[0.04em] transition-all hover:-translate-y-0.5 shadow-[0_14px_30px_-12px_rgba(15,23,42,0.55)]">
            {nav.cta_label || 'Get Instant Access'}
            <span className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center transition-transform group-hover:translate-x-0.5"><ArrowRight size={13} /></span>
          </button>
        </div>

        <button onClick={() => setOpen(o => !o)} className="md:hidden p-2 -mr-2 rounded-xl text-slate-900 hover:bg-slate-900/5 transition" aria-label="Menu" aria-expanded={open} data-testid="landing-menu-btn">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-900/5 bg-[#f5f1eb]/95 backdrop-blur-xl px-4 pb-5 pt-2 animate-slide-down" data-testid="landing-menu">
          {links.map(l => (
            <button key={l.target} onClick={() => go(l.target)} data-testid={`nav-mobile-${l.target}`}
              className="w-full flex items-center justify-between px-3 py-3.5 text-left text-[15px] font-semibold text-slate-800 border-b border-slate-900/5 last:border-0">
              {l.label}<ArrowRight size={15} className="text-slate-400" />
            </button>
          ))}
          <div className="mt-4 flex flex-col gap-2.5">
            <button onClick={() => { setOpen(false); onCta('header_mobile'); }} data-testid="landing-menu-cta"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 text-white text-[14px] font-extrabold uppercase tracking-[0.04em]">
              {nav.cta_label || 'Get Instant Access'}<ArrowRight size={15} />
            </button>
            <Link to="/login" onClick={() => setOpen(false)} className="w-full text-center px-6 py-3 rounded-full border border-slate-300 text-[14px] font-semibold text-slate-700" data-testid="landing-menu-login">{nav.login_label || 'Log in'}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
